import type { ReactNode } from "react";

/** The review row's icons: inline stroke SVG in the text colour, decorative unless labelled by their button. */

function Icon({ size = 16, width = 2, children }: { size?: number; width?: number; children: ReactNode }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={width}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

export const CheckIcon = ({ size = 18 }: { size?: number }) => (
  <Icon size={size} width={2.75}>
    <path d="M5 12.5l4.5 4.5L19 7.5" />
  </Icon>
);

export const CrossIcon = () => (
  <Icon size={14} width={2.5}>
    <path d="M6 6l12 12M18 6L6 18" />
  </Icon>
);

export const GridIcon = () => (
  <Icon size={16}>
    <rect x="4" y="4" width="6.5" height="6.5" rx="1.5" />
    <rect x="13.5" y="4" width="6.5" height="6.5" rx="1.5" />
    <rect x="4" y="13.5" width="6.5" height="6.5" rx="1.5" />
    <rect x="13.5" y="13.5" width="6.5" height="6.5" rx="1.5" />
  </Icon>
);

export const ArrowUpRightIcon = () => (
  <Icon size={9} width={2.5}>
    <path d="M7 17L17 7M8 7h9v9" />
  </Icon>
);

export const MinusIcon = () => (
  <Icon size={14} width={2.5}>
    <path d="M6 12h12" />
  </Icon>
);

export const PlusIcon = () => (
  <Icon size={14} width={2.5}>
    <path d="M12 6v12M6 12h12" />
  </Icon>
);

export const RefreshIcon = ({ size = 14 }: { size?: number }) => (
  <Icon size={size} width={2.25}>
    <path d="M20 12a8 8 0 1 1-2.34-5.66M20 4v5h-5" />
  </Icon>
);

export const ChevronLeftIcon = () => (
  <Icon size={20} width={2.25}>
    <path d="M15 18l-6-6 6-6" />
  </Icon>
);

export const ChevronDownIcon = ({ up = false }: { up?: boolean }) => (
  <Icon size={14} width={2.25}>
    <path d={up ? "M6 15l6-6 6 6" : "M6 9l6 6 6-6"} />
  </Icon>
);

export const EllipsisIcon = () => (
  <Icon size={18} width={2.5}>
    <path d="M5.5 12h.01M12 12h.01M18.5 12h.01" />
  </Icon>
);

export const TagIcon = () => (
  <Icon size={16}>
    <path d="M3.5 12.2V4.5a1 1 0 0 1 1-1h7.7l8.3 8.3a1 1 0 0 1 0 1.4l-7.4 7.4a1 1 0 0 1-1.4 0z" />
    <circle cx="8" cy="8" r="1.4" />
  </Icon>
);

export const BoxIcon = () => (
  <Icon size={16}>
    <path d="M3.5 7.5L12 3l8.5 4.5v9L12 21l-8.5-4.5z" />
    <path d="M3.5 7.5L12 12l8.5-4.5M12 12v9" />
  </Icon>
);

export const ChartIcon = () => (
  <Icon size={16}>
    <path d="M4 19.5h16M5 15.5l4.5-4.5 3.5 3 6-6.5" />
    <path d="M15 7.5h4v4" />
  </Icon>
);

export const ArchiveIcon = ({ size = 16 }: { size?: number }) => (
  <Icon size={size}>
    <rect x="3" y="4" width="18" height="4.5" rx="1" />
    <path d="M5 8.5V19a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V8.5M10 12.5h4" />
  </Icon>
);

export const CameraIcon = () => (
  <Icon size={20} width={1.8}>
    <path d="M4 8.5h3l1.5-2h7l1.5 2h3v10H4z" />
    <circle cx="12" cy="13.2" r="3.3" />
  </Icon>
);

export const BoltIcon = ({ on = false }: { on?: boolean }) => (
  <svg
    width={16}
    height={16}
    viewBox="0 0 24 24"
    fill={on ? "currentColor" : "none"}
    stroke="currentColor"
    strokeWidth={2}
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M13.5 2.5 4.5 13.5h6.5l-1 8 9-11h-6.5z" />
  </svg>
);

export const SearchIcon = () => (
  <Icon size={15}>
    <circle cx="10.5" cy="10.5" r="6" />
    <path d="M15 15l5 5" />
  </Icon>
);

export const ClearIcon = () => (
  <svg width={16} height={16} viewBox="0 0 24 24" aria-hidden="true">
    <circle cx="12" cy="12" r="10" fill="currentColor" />
    <path d="M8.5 8.5l7 7M15.5 8.5l-7 7" stroke="var(--color-app)" strokeWidth={2.25} strokeLinecap="round" />
  </svg>
);

export const PhotoIcon = () => (
  <Icon size={22} width={1.6}>
    <rect x="3.5" y="5" width="17" height="14" rx="2" />
    <circle cx="9" cy="10" r="1.6" />
    <path d="M4 17l5-4.5 3.5 3 3-2.5 4.5 4" />
  </Icon>
);
